import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState, useEffect, useCallback} from 'react';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ListingSettingsDropdown from 'nav/ListingSettingsDropdown';
import {AVAILABLE_PAGE_SIZES} from 'nav/sidepanel/PageSizeUtil';
import {FilterOption} from 'search/filters/base/FilterComponents';
import ListingFiltersContainer from 'search/filters/ListingFiltersContainer';
import * as GlobalSearchActions from 'search/GlobalSearchActions';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {FilterKey, Filter, Operator, SearchType, SortKey} from 'search/SearchConstants';
import * as Colors from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import ResultsPager from 'ui-library/ResultsPager';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import * as DateUtil from 'util/DateUtil';
import {InputChangeEvent} from 'util/Types';

import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';
import {ConnectionFileStatus, getConnectionFileStatus, fileStatusDisplay} from './ConnectionFilesStatus';

const styles = StyleSheet.create({
  tableContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
  },
  table: {
    borderLeft: 0,
    borderRight: 0,
    marginBottom: 0,
    marginRight: 0,
    marginLeft: 0,
  },
  tableRow: {
    height: '36px',
  },
  tableCell: {
    padding: '0 13px',
  },
  checkbox: {
    accentColor: Colors.BLUE,
  },
  checkboxCell: {
    textAlign: 'center',
    padding: 0,
    width: '20px',
    marginTop: '16px',
  },
  searchFilter: {
    width: '350px',
    marginRight: '10px',
  },
});
function ConnectionFilesTableCell(props: {
  className?: string;
  children: JSX.Element;
  onSelectRow: () => void;
}): JSX.Element {
  return (
    <Cell className={props.className} onClick={props.onSelectRow}>
      {props.children}
    </Cell>
  );
}

ConnectionFilesTableCell.defaultProps = {
  className: css(styles.tableCell),
};

const DEFAULT_PAGE_SIZE = 10;

const COLUMN_HEADER_TO_CELL: {
  [columnHeader: string]: (
    connectionFile: FileListingGraphQL,
    props: ConnectionFilesTableProps,
    onRefetchComplete: () => Promise<void>
  ) => JSX.Element | string | undefined;
} = {
  'File name': (connectionFile: FileListingGraphQL, props: ConnectionFilesTableProps) => {
    const filename = connectionFile.name || connectionFile.scratchFile.filename;
    if (props.onSelectRow && props.hideCheckboxColumn) {
      return <a>{filename}</a>;
    }
    return filename;
  },
  'Modified at': (connectionFile: FileListingGraphQL) => DateUtil.formalTime(connectionFile.modifiedAt),
  'Created at': (connectionFile: FileListingGraphQL) => DateUtil.formalTime(connectionFile.createdAt),
  Status: (connectionFile: FileListingGraphQL) => fileStatusDisplay(connectionFile, true),
};

function getFilters(connectionIds: Array<string>): Array<Filter> {
  const filters: Array<Filter> = [
    {
      key: FilterKey.TYPES,
      operator: Operator.IS_ONE_OF,
      value: [ModelTypeConstants.FILE],
    },
    {
      key: FilterKey.LINEAGE_PARENT_API_IDENTIFIER,
      operator: Operator.IS_ONE_OF,
      value: connectionIds,
    },
    {
      key: FilterKey.ARCHIVE_PURPOSES,
      operator: Operator.IS_ONE_OF,
      value: [NOT_ARCHIVED_PURPOSE_STRING],
    },
  ];
  return filters;
}

export interface ConnectionFilesTableProps {
  connectionFiles: Array<FileListingGraphQL>;
  setConnectionFiles: (connectionFiles: Array<FileListingGraphQL>) => void;
  selectedRowIndices?: Set<number>;
  onSelectRow?: (rowIndex: number) => void;
  allowMultiple?: boolean;
  columns: Array<keyof typeof COLUMN_HEADER_TO_CELL>;
  connectionIds: Array<string>;
  hideCheckboxColumn?: boolean;
  children?: React.ReactNode;
  showLineageParentFilter?: boolean;
}

export default function ConnectionFilesTable(props: ConnectionFilesTableProps): JSX.Element {
  const {selectedRowIndices, onSelectRow, setConnectionFiles} = props;
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [numTotalFiles, setNumTotalFiles] = useState(0);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [filters, setFilters] = useState<Array<Filter>>(getFilters(props.connectionIds));
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const filterOptions: Array<FilterOption> = [
    {filterKey: FilterKey.CREATED_AT},
    {filterKey: FilterKey.MODIFIED_AT},
    {filterKey: FilterKey.STARRED},
  ];
  if (props.showLineageParentFilter) {
    filterOptions.push({filterKey: FilterKey.LINEAGE_PARENT_API_IDENTIFIER});
  }

  const fetchFiles = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    const response = (await GlobalSearchActions.doGlobalSearch(
      {
        offset,
        limit: pageSize,
        sorts: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: true}],
        filters,
        query: searchFilter,
      },
      SearchType.GLOBAL
    )) as GlobalSearchResponse;

    setConnectionFiles(Object.values(response.filesById));
    setIsLoading(false);
    setNumTotalFiles(response.total);
  }, [filters, offset, searchFilter, setConnectionFiles, pageSize]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  return (
    <div>
      <div className="u-flex-display u-spacing-bottom">
        <PlainSearchInput
          className={css(styles.searchFilter)}
          clearType={ClearType.X}
          onEscape={() => {
            setSearchFilter('');
          }}
          onChange={(e: InputChangeEvent) => {
            setOffset(0);
            setSearchFilter(e.target.value);
          }}
          placeholder="Search"
          value={searchFilter}
        />
        <div className="u-flex-display u-align-items-center">
          <ListingFiltersContainer
            filters={filters}
            primaryFilterOptions={[
              {
                filterKey: FilterKey.ARCHIVE_PURPOSES,
                extraProps: {archivePurposes: BenchlingConfig.ARCHIVE_NON_EXPENDABLE_PURPOSES},
              },
            ]}
            secondaryFilterProps={{
              filterGroups: [
                {
                  name: 'General',
                  filterOptions,
                },
              ],
              isDropdownOpen,
              onSetDropdownOpen: setIsDropdownOpen,
            }}
            selectedSchema={null}
            onChangeFilters={(filters: Array<Filter>) => {
              setOffset(0);
              setFilters(filters);
            }}
          />
        </div>
      </div>
      <div className="u-flex-display u-align-items-center">
        <ResultsPager
          isLoading={isLoading}
          setOffset={setOffset}
          pagination={{
            offset,
            limit: pageSize,
            total: numTotalFiles,
            isExact: true,
          }}
        />
        <ListingSettingsDropdown
          pageSizeConfig={{
            availablePageSizes: AVAILABLE_PAGE_SIZES,
            pageSize,
            onSetPageSize: (newPageSize: number) => {
              setOffset(0);
              setPageSize(newPageSize);
            },
          }}
        />
      </div>
      {isLoading ? (
        <CenteredHorizontalThrobber />
      ) : (
        <>
          <div className={css(styles.tableContainer)}>
            <Table
              emptyTableText="No files found"
              numRows={props.connectionFiles.length}
              overflowVisible={true}
              className={css(styles.table)}
              getRowClassName={() => css(styles.tableRow)}
            >
              {selectedRowIndices != null && onSelectRow != null && !props.hideCheckboxColumn ? (
                <Column
                  header={
                    <Cell className={css(styles.checkboxCell)}>
                      <IndeterminateCheckbox
                        items={[...Array(props.connectionFiles.length).keys()]}
                        selectedMap={(idx: number): boolean => selectedRowIndices.has(idx)}
                        onChange={() => {
                          [...Array(props.connectionFiles.length).keys()].forEach((i) => {
                            if (selectedRowIndices.size === 0 || selectedRowIndices.has(i)) {
                              onSelectRow(i);
                            }
                          });
                        }}
                      />
                    </Cell>
                  }
                  renderCell={(rowIndex: number) => (
                    <ConnectionFilesTableCell
                      className={css(styles.checkboxCell)}
                      onSelectRow={() => {
                        onSelectRow(rowIndex);
                      }}
                    >
                      <input
                        type="checkbox"
                        className={css(styles.checkbox)}
                        disabled={
                          getConnectionFileStatus(props.connectionFiles[rowIndex], true) !==
                            ConnectionFileStatus.UPLOADED ||
                          (!props.allowMultiple &&
                            selectedRowIndices.size > 0 &&
                            !selectedRowIndices.has(rowIndex))
                        }
                        checked={selectedRowIndices.has(rowIndex)}
                        onChange={
                          noop /* Required to avoid React warning, even though we are controlling the change at the cell level */
                        }
                      />
                    </ConnectionFilesTableCell>
                  )}
                />
              ) : null}
              {props.columns.map((header, i) => (
                <Column
                  key={i}
                  header={header}
                  renderCell={(rowIndex: number) => (
                    <ConnectionFilesTableCell
                      onSelectRow={
                        onSelectRow
                          ? () => {
                              onSelectRow(rowIndex);
                            }
                          : noop
                      }
                    >
                      <>{COLUMN_HEADER_TO_CELL[header](props.connectionFiles[rowIndex], props, fetchFiles)}</>
                    </ConnectionFilesTableCell>
                  )}
                />
              ))}
            </Table>
          </div>
          {props.children}
        </>
      )}
    </div>
  );
}
